import React, { useState } from "react";
import { Tabs } from "subframe/components/Tabs";
import { TaskCenterFilters, TaskCenterTable } from "./components";
import { taskCenterData } from "./data";
import { BreadcrumbItem, TaskCenterFilter, TaskCenterTab } from "../../types";
import { Breadcrumb } from "../EmployeeProfile/components";
import { IconName } from "../../types";
import RETENTION_ROUTES from "modules/Retention/constants/routes";
import { useQuery } from "@tanstack/react-query";
import endpoints from "modules/Retention/constants/endpoints";
import { getEscalations } from "modules/Retention/services/retention";
import { IconButton } from "subframe/components/IconButton";
import { getDateRangeFromTimeRange } from "utils/helper";



export default function TaskCenterPage() {
  const [status, setStatus] = useState<string>('');
  const [filters, setFilters] = useState<TaskCenterFilter>(taskCenterData.filters);
  const [activeTab, setActiveTab] = useState<TaskCenterTab>(TaskCenterTab.ESCALATIONS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Get date range from timeRange filter
  const dateRange = getDateRangeFromTimeRange(filters.timeRange);

  // Prepare API parameters
  const apiParams: any = {
    status: status,
    sort_by: filters.rankBy,
    page: currentPage,
    page_size: pageSize,
    type: activeTab,
    ...dateRange
  };

  // Only include location if it's not "all"
  if (filters.branch && filters.branch !== "all") {
    apiParams.location = filters.branch;
  }

  const {data, isLoading} = useQuery<any>({
    queryKey: [endpoints.escalations, status, filters.timeRange, filters.rankBy, filters.branch, currentPage, activeTab],
    queryFn: () => getEscalations(apiParams)
  })

  const handleFilterChange = (newFilters: TaskCenterFilter) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleTabChange = (newTab: TaskCenterTab) => {
    setActiveTab(newTab);
    // Reset filters to default when changing tabs
    setFilters(taskCenterData.filters);
    setStatus('');
    setCurrentPage(1);
  };

  const handleFilterReset = (newFilters: TaskCenterFilter) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const isEscalationsTab = activeTab === TaskCenterTab.ESCALATIONS;
  const isAnalysisTab = activeTab === TaskCenterTab.ANALYSIS;

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      icon: IconName.FEATHER_HOME,
      label: "Homepage",
      href: RETENTION_ROUTES.HOME
    },
    {
      icon: IconName.FEATHER_LIST_CHECKS,
      label: "Task Center",
      href: RETENTION_ROUTES.TASK_CENTER
    }
  ];

  const pagination = data?.result?.pagination;

  return (
    <div className="flex h-full w-full flex-col items-start gap-2">
      <Breadcrumb items={breadcrumbItems} />
      <div className="flex w-full max-w-[1280px] grow shrink-0 basis-0 flex-col items-start gap-4 md:gap-8 bg-default-background px-4 pt-6 pb-12">
        <div className="flex w-full flex-col items-start gap-4">
          <Tabs>
            <Tabs.Item
              icon="FeatherFlagTriangleRight"
              active={isEscalationsTab}
              onClick={() => handleTabChange(TaskCenterTab.ESCALATIONS)}
            >
              Escalations
            </Tabs.Item>
            <Tabs.Item
              active={isAnalysisTab}
              icon="FeatherBarChartHorizontal"
              onClick={() => handleTabChange(TaskCenterTab.ANALYSIS)}
            >
              Escalation Analysis
            </Tabs.Item>
          </Tabs>
        </div>

        <TaskCenterFilters
          filters={filters}
          status={status}
          setStatus={setStatus}
          onFilterChange={handleFilterChange}
          onFilterReset={handleFilterReset}
          showTimeFilter={isAnalysisTab}
          showAllStats={isAnalysisTab}
          activeTab={activeTab}
        />
        
        <TaskCenterTable rows={data?.result?.escalations as any ?? []} filters={filters} loading={isLoading} />
        
        {pagination && (
          <div className="flex w-full flex-col items-center gap-4 pt-2">
            <div className="flex max-w-[1280px] items-center justify-end gap-1">
              <IconButton
                variant="neutral-secondary"
                icon="FeatherChevronLeft"
                disabled={!pagination.has_previous}
                onClick={() => handlePageChange(pagination.page - 1)}
              />
              <div className="flex h-8 items-center justify-center gap-1 bg-neutral-100 px-2 py-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  {pagination.page} of {pagination.total_pages}
                </span>
              </div>
              <IconButton
                variant="neutral-secondary"
                icon="FeatherChevronRight"
                disabled={!pagination.has_next}
                onClick={() => handlePageChange(pagination.page + 1)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 